import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { ApiError } from '@greencandle/api-client'
import { AuthProvider, useAuth } from './auth'
import { router } from './routes'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Portfolio figures change at most once a day for prices and FX; a minute of staleness
      // costs nothing and stops every tab focus refetching the whole dashboard.
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // A 4xx is an answer, not a blip. Only retry what might succeed if asked again, and
        // never a 401 - the client already tried a token refresh before it surfaced.
        if (error instanceof ApiError && error.status < 500) {
          return false
        }
        return failureCount < 2
      },
    },
  },
})

/**
 * Auth used to be an app-level switch, entirely outside the router: `user ? <RouterProvider/> :
 * <Login/>`. That worked, but it meant `/login` was never a real URL - the address bar kept
 * whatever it last showed while a completely different tree rendered over it - and a signed-out
 * visit to a deep link like `/transactions` had no way to return there after signing in, because
 * the router never got to see that URL at all.
 *
 * <p>The router now owns that decision: `protectedRoute`'s `beforeLoad` (routes.tsx) redirects to
 * `/login` itself when `context.auth.user` is empty, carrying the original location so sign-in
 * can return to it. All this component still does is wait out the one-time "is the stored token
 * still good" check before handing the router a `context` to make that decision with - handing it
 * one prematurely would have the guard redirect based on an auth state that has not been
 * resolved yet.
 */
function Root() {
  const auth = useAuth()

  if (auth.loading) {
    return (
      <div className="centered">
        <span className="muted">Checking your session…</span>
      </div>
    )
  }

  return <RouterProvider router={router} context={{ auth }} />
}

const container = document.getElementById('root')
if (!container) {
  throw new Error('index.html is missing #root')
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
